import api from './api';
import authService from '@/api/authService';
import { createSocketCache, waitForDelete, waitForUpdate } from '@/api/socketConnection';
import userApi from '@/api/userApi';
import service from '@/lib/service';
import { AggregatedBucket } from 'common/types/aggregatedBucket';
import { BucketQuery } from 'common/types/bucketQuery';
import { WorkflowState } from 'common/types/workflow';
import { createCache } from 'cross-state';
import { groupBy, orderBy } from 'lodash';
import { debounce } from 'radashi';
import { z } from 'zod';

const bucketApi = service(
    'bucketApi',
    class BucketApi {
        debouncedInvalidate = debounce({ delay: 1000 }, () => {
            this.list.invalidateAll();
        });

        list = createSocketCache<AggregatedBucket[], [query?: BucketQuery]>({
            async fetch(query = {}) {
                const response = await api.post(`/api/buckets`, { json: query }).json();
                return z.array(AggregatedBucket).parse(response);
            },
            onBucketUpdate: (bucket, cache) => {
                for (const instance of cache.getInstances()) {
                    const value = instance.state.get().value;
                    const index = value?.findIndex((b) => b.id === bucket.id) ?? -1;

                    if (!value || index === -1) {
                        continue;
                    }

                    const newValue = [...value];
                    newValue[index] = bucket;
                    instance.updateValue(newValue);
                }

                this.debouncedInvalidate();
            },
            onBucketDelete: (_msg, cache) => {
                for (const instance of cache.getInstances()) {
                    const value = instance.state.get().value;
                    const index = value?.findIndex((b) => b.id === _msg.bucketId) ?? -1;

                    if (!value || index === -1) {
                        continue;
                    }

                    const newValue = [...value];
                    newValue.splice(index, 1);
                    instance.updateValue(newValue);
                }

                this.debouncedInvalidate();
            },
        });

        byPerson = createCache<Record<string, AggregatedBucket[]>, [query?: BucketQuery]>(
            (query = {}) =>
                async ({ use }) => {
                    const [buckets, users, session] = await Promise.all([
                        use(this.list(query)),
                        use(userApi.list),
                        use(authService.appLogin),
                    ]);

                    const usersLookup = new Map(users.map((u) => [u.id, u]));

                    const bucketsOrdered = orderBy(
                        buckets,
                        [
                            (bucket) => (bucket.ownerId === session?.user.id ? 1 : 0),
                            (bucket) => usersLookup.get(bucket.ownerId)?.name,
                            (group) =>
                                group.type === 'trip'
                                    ? group.tripData.endedOn
                                    : group.receipts[0].date,
                        ],
                        ['asc', 'asc', 'asc'],
                    );

                    const bucketsByPerson = groupBy(bucketsOrdered, (bucket) => bucket.ownerId);

                    return bucketsByPerson;
                },
            {
                getCacheKey(query?) {
                    return BucketQuery.parse(query);
                },
            },
        );

        async transition(
            bucketId: string,
            targetState: WorkflowState,
            { comment, awaitUpdate = true }: { comment?: string; awaitUpdate?: boolean } = {},
        ) {
            const response = api
                .put(`/api/buckets/${bucketId}/state/${targetState}`, {
                    json: { comment },
                })
                .json();

            if (!awaitUpdate) {
                await response;
                return;
            }

            await waitForUpdate(() => response);
        }

        async archive(bucketId: string, value: boolean) {
            await waitForUpdate(() => {
                return api.put(`/api/buckets/${bucketId}/archive/${value}`).json();
            });
        }

        async delete(bucketId: string) {
            await waitForDelete(bucketId, () => {
                return api.delete(`/api/buckets/${bucketId}`).json();
            });
        }

        async rehash(bucketId: string) {
            await waitForUpdate(() => {
                return api.post(`/api/buckets/${bucketId}/rehash`).json();
            });
        }
    },
);
export default bucketApi;
